import {  AuthState } from "@okta/okta-auth-js";
import * as React from "react";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { validateAuthState } from "../../Auth/AuthHelper";
import i18n from "../../i18n";
import { IStoreState } from "../../Model";
import { Error } from "../../Shared/Error";
import { LoadingIndicator } from "../../Shared/LoadingIndicator";
import Pagination from "../../Shared/Pagination/Index";
import UpdateList from "../components/Updates";
import { getUpdateListThunk, setSortOrder, resetSortOrder, clearAllFilters } from "../duck/Actions";
import { WithOktaAuthProps } from "src/ts/Auth/duck/Models";
import { withOktaAuth } from "@okta/okta-react";
import { IUpdateRequestParams } from "../duck/Operations";

export interface IUpdateStateProps {
  failed: boolean;
  fetched: boolean;
  fetching: boolean;
  updates: Api.IPagedResult<Api.INotificationViewModel>;
  orderBy: string;
  filters: any;
  filteredResults: Api.INotificationViewModel[];
}

interface IUpdatePageDispatchProps {
  fetchUpdateList: (auth: AuthState | null, requestParams: IUpdateRequestParams) => any;
  setSortOrder: (orderBy: string) => void;
  resetSortOrder: () => void;
  clearAllFilters: () => void;
}

interface IUpdatePageOwnProps { currentPage: number };


type IUpdateProps = IUpdateStateProps & IUpdatePageDispatchProps & IUpdatePageOwnProps & WithOktaAuthProps;

class UpdatePage extends React.Component<IUpdateProps> {
  
  constructor(props: IUpdateProps) {
    super(props);
  }

  private buildRequestParams(): IUpdateRequestParams {
    return {
      pageNumber: this.props.currentPage,
      pageSize: this.props.updates.pageSize,
      skip: (this.props.currentPage - 1) * this.props.updates.pageSize,
      filter: { filters: this.props.filters },
      orderBy: this.props.orderBy
    };
  }

  public componentDidMount() {
    if (this.props.authState?.isAuthenticated) {
      // Always reset sort order on initial page load/navigation
      this.props.resetSortOrder();
      this.props.fetchUpdateList(this.props.authState, this.buildRequestParams());
    }
    else {
      validateAuthState(this.props.authState);
    }
  }

  public componentDidUpdate(prevProps: IUpdateProps) {
    if (this.props.authState?.isAuthenticated) {
      // Don't make another API call if one is already in progress
      // This prevents the race condition when page size changes trigger both
      // a direct API call from Pagination and a URL change that triggers componentDidUpdate
      if (this.props.fetching) {
        return;
      }
      if (prevProps.currentPage !== this.props.currentPage ||
          prevProps.orderBy !== this.props.orderBy) {
        this.props.fetchUpdateList(this.props.authState, this.buildRequestParams());
      }
      else if (prevProps.authState === null && this.props.authState?.isAuthenticated) {
        this.props.fetchUpdateList(this.props.authState, this.buildRequestParams());
      }
    }
    else {
      validateAuthState(this.props.authState);
    }
  }

  public componentWillUnmount() {
    // Reset sorting and filters when navigating away from the Updates tab
    this.props.resetSortOrder();
    this.props.clearAllFilters();
  }

  public handleSortChange = (orderBy: string) => {
    this.props.setSortOrder(orderBy);
  }

  // Wrapper function for Pagination component that expects (pageNumber, itemPerPage, auth) signature
  public handlePageSizeChange = (pageNumber: number, itemPerPage: number, auth: AuthState | null) => {
    if (auth) {
      const requestParams: IUpdateRequestParams = {
        pageNumber: pageNumber,
        pageSize: itemPerPage,
        skip: (pageNumber - 1) * itemPerPage,
        filter: { filters: this.props.filters },
        orderBy: this.props.orderBy
      };
      this.props.fetchUpdateList(auth, requestParams);
    }
  }

  public render() {
    if (!this.props.authState) {
      return <LoadingIndicator busy={true} />
    }
    if (this.props.authState?.error) {
      throw this.props.authState.error;
    }
    return (
      <React.Fragment>
        {this.props.failed && <Error message={i18n.t("home.fetchingUpdatesFailed")} />}
        <LoadingIndicator busy={this.props.fetching} />
        {this.props.fetched && <UpdateList updates={this.props.updates} onSortChange={this.handleSortChange} orderBy={this.props.orderBy} />}
        {this.props.fetched && this.props.updates.rowCount > 0 && <Pagination onPageSizeChange={this.handlePageSizeChange}
          pageSize={this.props.updates.pageSize}
          currentPage={this.props.currentPage}
          linkUrlLeftPart="/home/updates" totalRecordsNumber={this.props.updates.rowCount}
          pageCount={this.props.updates.pageCount}
          authState={this.props.authState} />}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IStoreState) => {
  return {
    failed: state.updates.failed,
    fetched: state.updates.fetched,
    fetching: state.updates.fetching,
    updates: state.updates.updates,
    orderBy: state.updates.orderBy,
    filters: state.updates.filters,
    filteredResults: state.updates.filteredResults
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    fetchUpdateList: (auth: AuthState, requestParams: IUpdateRequestParams) => {
      dispatch(getUpdateListThunk(auth, requestParams));
    },
    setSortOrder: (orderBy: string) => {
      dispatch(setSortOrder(orderBy));
    },
    resetSortOrder: () => {
      dispatch(resetSortOrder());
    },
    clearAllFilters: () => {
      dispatch(clearAllFilters() as any);
    }
  };
};

const hoc = compose(withOktaAuth, connect<IUpdateStateProps, IUpdatePageDispatchProps, IUpdatePageOwnProps>(mapStateToProps, mapDispatchToProps))(UpdatePage);

export { hoc as UpdatePage };
